/**
 * parseClarifyingMarkdown.ts — BUG-DR-CLARIFYING-CARD-JSX-MISSING (2026-05-20)
 *
 * Defensive fallback: synthesise a ClarifyingQuestionsStagePayload from the
 * markdown body that renderClarifyingCardMarkdown (functions/src/prest0nSession/
 * clarifyingPreStage.ts:266) emits as the visible chat message. The frontend
 * already has a structured branch in useGlobalTools.ts:2058 that consumes
 * `currentStage.payload` to render the Stage-1 card with the green
 * Skip-to-Deep-Research button; if the payload field is ever stripped (e.g.
 * Firestore arrayUnion edge cases, a future Prest0nTaskRecord serializer
 * regression, or a stale frontend cache that predates the payload contract),
 * this helper lets the same renderer fire by parsing the always-present
 * markdown body instead.
 *
 * The renderClarifyingCardMarkdown shape (mirrored exactly here):
 *
 *   **Initial findings**
 *   <prose>
 *
 *   **Clarifying questions**
 *   1. Q1 _(context1)_
 *   2. Q2 _(context2)_
 *   …
 *
 *   👉 Click **Skip to Deep Research** above, or reply `skip` or `proceed`
 *   to go straight to the full research.
 *
 * Spanish variant swaps the headings to "Hallazgos iniciales" /
 * "Preguntas aclaratorias" and the footer to "Haz clic en **Ir directo a
 * la investigación** arriba …".
 *
 * Pure function — no React, no firebase imports — so it can be unit-tested
 * standalone (see src/components/GlobalChatSystem/__tests__/
 * taskQueueClarifyingCard.test.ts).
 */

export interface ParsedClarifyingPayload {
  initialFindings: string;
  clarifyingQuestions: Array<{ question: string; context: string }>;
  language: 'en' | 'es';
}

/** Heading + footer constants — kept literal so future renames break tests
 *  loudly instead of silently. Mirror clarifyingPreStage.ts:266-298. */
const HEADING_FINDINGS_EN = '**Initial findings**';
const HEADING_FINDINGS_ES = '**Hallazgos iniciales**';
const HEADING_QUESTIONS_EN = '**Clarifying questions**';
const HEADING_QUESTIONS_ES = '**Preguntas aclaratorias**';
const FOOTER_SIGNATURE_EN = 'Click **Skip to Deep Research** above';
const FOOTER_SIGNATURE_ES = 'Haz clic en **Ir directo a la investigación** arriba';

/**
 * Attempt to extract a clarifying payload from the markdown body of a
 * Stage-1 clarifying-questions tool result. Returns null when the input
 * doesn't match the expected shape (any other markdown should fall through
 * untouched so we don't mis-render generic assistant text as a card).
 */
export function parseClarifyingMarkdownToPayload(
  markdown: unknown,
): ParsedClarifyingPayload | null {
  if (typeof markdown !== 'string') return null;
  const text = markdown.trim();
  if (!text) return null;

  // Language detection: prefer ES if any ES marker is present; the LLM emits
  // each pre-stage in a single language so mixed-language bodies are
  // out-of-scope.
  const isEs =
    text.includes(HEADING_FINDINGS_ES) ||
    text.includes(HEADING_QUESTIONS_ES) ||
    text.includes(FOOTER_SIGNATURE_ES);
  const language: 'en' | 'es' = isEs ? 'es' : 'en';
  const findingsHeading = isEs ? HEADING_FINDINGS_ES : HEADING_FINDINGS_EN;
  const questionsHeading = isEs ? HEADING_QUESTIONS_ES : HEADING_QUESTIONS_EN;

  // Pre-condition: at least one heading must appear. The markdown renderer
  // always emits the questions heading when ≥1 question exists, and the
  // findings heading when initialFindings is non-empty. We require ONE of
  // them or we conservatively return null (don't mis-classify random text).
  const hasFindingsHeading = text.includes(findingsHeading);
  const hasQuestionsHeading = text.includes(questionsHeading);
  if (!hasFindingsHeading && !hasQuestionsHeading) return null;

  // ──────────────────────────────────────────
  // Findings block: heading line → next blank line OR next heading OR footer.
  // ──────────────────────────────────────────
  let initialFindings = '';
  if (hasFindingsHeading) {
    const findIdx = text.indexOf(findingsHeading);
    const afterFindings = text.slice(findIdx + findingsHeading.length);
    const stopCandidates: number[] = [];
    if (hasQuestionsHeading) {
      const qStop = afterFindings.indexOf(questionsHeading);
      if (qStop >= 0) stopCandidates.push(qStop);
    }
    const footerSig = isEs ? FOOTER_SIGNATURE_ES : FOOTER_SIGNATURE_EN;
    const footerStop = afterFindings.indexOf(footerSig);
    if (footerStop >= 0) stopCandidates.push(footerStop);
    const cutAt = stopCandidates.length > 0 ? Math.min(...stopCandidates) : afterFindings.length;
    initialFindings = afterFindings.slice(0, cutAt).trim();
  }

  // ──────────────────────────────────────────
  // Questions block: heading line → next blank-line+footer OR end-of-text.
  // Parse "N. <question> _(<context>)_" lines.
  // ──────────────────────────────────────────
  const clarifyingQuestions: ParsedClarifyingPayload['clarifyingQuestions'] = [];
  if (hasQuestionsHeading) {
    const qIdx = text.indexOf(questionsHeading);
    const afterQuestions = text.slice(qIdx + questionsHeading.length);
    const footerSig = isEs ? FOOTER_SIGNATURE_ES : FOOTER_SIGNATURE_EN;
    const footerStop = afterQuestions.indexOf(footerSig);
    const emojiStop = afterQuestions.indexOf('👉');
    const stops = [footerStop, emojiStop].filter((i) => i >= 0);
    const cutAt = stops.length > 0 ? Math.min(...stops) : afterQuestions.length;
    const block = afterQuestions.slice(0, cutAt).trim();

    // Match numbered list lines: "1. Question text"
    // The optional trailing " _(context)_" italicises the context per the
    // markdown renderer. We extract it separately so the card UI can render
    // the question prominently and the context as a hint.
    const lineRe = /^[ \t]*\d+\.[ \t]+(.+?)[ \t]*$/gm;
    let m: RegExpExecArray | null;
    while ((m = lineRe.exec(block)) !== null) {
      const raw = (m[1] || '').trim();
      if (!raw) continue;
      // Detect trailing "_(context)_" — markdown italics around parens.
      const ctxMatch = raw.match(/_\(([^)]+)\)_\s*$/);
      const question = ctxMatch
        ? raw.slice(0, ctxMatch.index).replace(/\s+$/, '').trim()
        : raw;
      const context = ctxMatch ? ctxMatch[1].trim() : '';
      if (question) clarifyingQuestions.push({ question, context });
    }
  }

  // If neither bucket yielded anything usable, treat the parse as a miss so
  // the caller falls back to plain-text rendering rather than a blank card.
  if (!initialFindings.trim() && clarifyingQuestions.length === 0) return null;

  return { initialFindings: initialFindings.trim(), clarifyingQuestions, language };
}
